/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BrokerPayInvoice } from '../models/BrokerPayInvoice';
import type { CallbackMaib } from '../models/CallbackMaib';
import type { CancelReason } from '../models/CancelReason';
import type { CashBox } from '../models/CashBox';
import type { CashBoxDetail } from '../models/CashBoxDetail';
import type { ConfirmBankTransfer } from '../models/ConfirmBankTransfer';
import type { EnqueuedOffer } from '../models/EnqueuedOffer';
import type { Invoice } from '../models/Invoice';
import type { InvoiceDetail } from '../models/InvoiceDetail';
import type { InvoiceSendOverdue } from '../models/InvoiceSendOverdue';
import type { InvoiceUrl } from '../models/InvoiceUrl';
import type { ModificationPrice } from '../models/ModificationPrice';
import type { Order } from '../models/Order';
import type { OrderApprove } from '../models/OrderApprove';
import type { OrderApproveResponse } from '../models/OrderApproveResponse';
import type { OrderAssign } from '../models/OrderAssign';
import type { OrderCancel } from '../models/OrderCancel';
import type { OrderCheckout } from '../models/OrderCheckout';
import type { OrderDetail } from '../models/OrderDetail';
import type { OrderMediaCreate } from '../models/OrderMediaCreate';
import type { OrderMediaDetail } from '../models/OrderMediaDetail';
import type { OrderModify } from '../models/OrderModify';
import type { OrderOfferDetail } from '../models/OrderOfferDetail';
import type { OrderProduct } from '../models/OrderProduct';
import type { OrderReason } from '../models/OrderReason';
import type { OrderRequest } from '../models/OrderRequest';
import type { OrderUserDetail } from '../models/OrderUserDetail';
import type { PaginatedCancelReasonList } from '../models/PaginatedCancelReasonList';
import type { PaginatedCashBoxListList } from '../models/PaginatedCashBoxListList';
import type { PaginatedExchangeRateList } from '../models/PaginatedExchangeRateList';
import type { PaginatedInvoiceListList } from '../models/PaginatedInvoiceListList';
import type { PaginatedOrderListList } from '../models/PaginatedOrderListList';
import type { PaginatedOrderMediaDetailList } from '../models/PaginatedOrderMediaDetailList';
import type { PaginatedOrderOfferList } from '../models/PaginatedOrderOfferList';
import type { PaginatedOrderUserListList } from '../models/PaginatedOrderUserListList';
import type { PaginatedProductListList } from '../models/PaginatedProductListList';
import type { PaginatedTransactionListList } from '../models/PaginatedTransactionListList';
import type { PatchedCashBox } from '../models/PatchedCashBox';
import type { PatchedOrderEditRequest } from '../models/PatchedOrderEditRequest';
import type { PatchedProduct } from '../models/PatchedProduct';
import type { PayInvoice } from '../models/PayInvoice';
import type { PayResult } from '../models/PayResult';
import type { Price } from '../models/Price';
import type { PriceRequest } from '../models/PriceRequest';
import type { PriceResponse } from '../models/PriceResponse';
import type { Product } from '../models/Product';
import type { ProductDetail } from '../models/ProductDetail';
import type { SendCustomerNotification } from '../models/SendCustomerNotification';
import type { SendOfferNotification } from '../models/SendOfferNotification';
import type { StaffOrderAccidents } from '../models/StaffOrderAccidents';
import type { StaffOrderAccidentsCredit } from '../models/StaffOrderAccidentsCredit';
import type { StaffOrderTravelInsurance } from '../models/StaffOrderTravelInsurance';
import type { StaffOrderTravelInsuranceCredit } from '../models/StaffOrderTravelInsuranceCredit';
import type { StaffPayInvoice } from '../models/StaffPayInvoice';
import type { TransactionDetail } from '../models/TransactionDetail';
import type { TransactionStatus } from '../models/TransactionStatus';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class SalesService {

  /**
   * Callback registered in MAIB
   * @param requestBody 
   * @returns void 
   * @throws ApiError
   */
  public static salesCallbackMaibCreate(
requestBody?: CallbackMaib,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/callback/maib/',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        302: `No response body`,
      },
    });
  }

  /**
   * @param ordering Ordering fields: name, code, created, modified
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, code
   * @returns PaginatedCancelReasonList 
   * @throws ApiError
   */
  public static salesCancelReasonsList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedCancelReasonList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/cancel-reasons/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this Cancel Reason.
   * @returns CancelReason 
   * @throws ApiError
   */
  public static salesCancelReasonsRetrieve(
id: number,
): CancelablePromise<CancelReason> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/cancel-reasons/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param activeOnly 
   * @param available 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param name 
   * @param ordering Ordering fields: name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search 
   * @returns PaginatedCashBoxListList 
   * @throws ApiError
   */
  public static salesCashBoxesList(
activeOnly?: boolean,
available?: boolean,
excludeIds?: string,
ids?: string,
name?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedCashBoxListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/cash-boxes/',
      query: {
        'active_only': activeOnly,
        'available': available,
        'exclude_ids': excludeIds,
        'ids': ids,
        'name': name,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this cash box.
   * @returns CashBoxDetail 
   * @throws ApiError
   */
  public static salesCashBoxesRetrieve(
id: number,
): CancelablePromise<CashBoxDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/cash-boxes/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this cash box.
   * @param requestBody 
   * @returns CashBox 
   * @throws ApiError
   */
  public static salesCashBoxesPartialUpdate(
id: number,
requestBody?: PatchedCashBox,
): CancelablePromise<CashBox> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/sales/cash-boxes/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Активировать кассу. Автоматически деактивирует другие кассы того же ECEC.
   * @param id A unique integer value identifying this cash box.
   * @param requestBody 
   * @returns CashBoxDetail 
   * @throws ApiError
   */
  public static salesCashBoxesActivateCreate(
id: number,
requestBody: CashBox,
): CancelablePromise<CashBoxDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/cash-boxes/{id}/activate/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param currency 
   * @param date 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: date, currency
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedExchangeRateList 
   * @throws ApiError
   */
  public static salesExchangeRatesList(
currency?: string,
date?: string,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedExchangeRateList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/exchange-rates/',
      query: {
        'currency': currency,
        'date': date,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param author 
   * @param broker Filtrează facturile după ID-ul brokerului.
   * @param contractor Filtrează facturile după ID-ul entității contractante.
   * @param contractorType Filtrează facturile după tipul contractantului.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer Filtrează facturile după ID-ul clientului.
   * @param date 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludePaymentType Exclude facturile care au tipul de plată transmis.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param excludeStage Exclude facturile care au oricare dintre etapele transmise.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param excludeStatus Exclude facturile care au oricare dintre statusurile transmise.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param ids comma separated list
   * @param isMobile Filtrează facturile comenzilor create din aplicația mobilă (iOS sau Android).
   * @param isRegisteredUser Filtrează facturile pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param number 
   * @param order 
   * @param ordering Ordering fields: created, modified, date, order__product, series, number, _days_overdue
   * @param _package Filtrează facturile după pachetul casco.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param paidDate 
   * @param paymentType * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează facturile după numărul poliței.
   * @param product Filtrează facturile după ID-urile produselor.
   * @param search Search fields: order__id, order__product__name, order__user__email, order__user__first_name, order__user__last_name, order__customer__email, order__customer__first_name, order__customer__last_name, order__contractor__as_person__email, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_person__identification_number, order__contractor__as_legalentity__identification_number, order__contractor__as_legalentity__name, order__policy__number, order__source, order__author__first_name, order__author__last_name, order__broker__name
   * @param series 
   * @param source Filtrează facturile după sursa comenzii. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param stage Filtrează facturile după etapă.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param status Filtrează facturile după status.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param type * `payment` - Payment
 * * `refund` - Refund
   * @param user Filtrează facturile după ID-ul utilizatorului.
   * @param validToGte Filtrează facturile cu data mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează facturile cu data mai mică sau egală decât valoarea transmisă.
   * @returns PaginatedInvoiceListList 
   * @throws ApiError
   */
  public static salesInvoicesList(
author?: number,
broker?: number,
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
date?: string,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludePaymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
excludeStage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
excludeStatus?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
ids?: string,
isMobile?: boolean,
isRegisteredUser?: boolean,
number?: number,
order?: number,
ordering?: string,
_package?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
page?: number,
pageSize?: number,
paidDate?: string,
paymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
policyNumber?: boolean,
product?: Array<number>,
search?: string,
series?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
stage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
status?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
type?: 'payment' | 'refund',
user?: number,
validToGte?: string,
validToLte?: string,
): CancelablePromise<PaginatedInvoiceListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/invoices/',
      query: {
        'author': author,
        'broker': broker,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'date': date,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_payment_type': excludePaymentType,
        'exclude_stage': excludeStage,
        'exclude_status': excludeStatus,
        'ids': ids,
        'is_mobile': isMobile,
        'is_registered_user': isRegisteredUser,
        'number': number,
        'order': order,
        'ordering': ordering,
        'package': _package,
        'page': page,
        'page_size': pageSize,
        'paid_date': paidDate,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'search': search,
        'series': series,
        'source': source,
        'stage': stage,
        'status': status,
        'type': type,
        'user': user,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @returns InvoiceDetail 
   * @throws ApiError
   */
  public static salesInvoicesRetrieve(
id: number,
): CancelablePromise<InvoiceDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/invoices/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @param requestBody 
   * @returns PayResult 
   * @throws ApiError
   */
  public static salesInvoicesBrokerPayCreate(
id: number,
requestBody?: BrokerPayInvoice,
): CancelablePromise<PayResult> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/broker-pay/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @returns InvoiceDetail 
   * @throws ApiError
   */
  public static salesInvoicesCancelCreate(
id: number,
): CancelablePromise<InvoiceDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/cancel/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @param requestBody 
   * @returns InvoiceDetail 
   * @throws ApiError
   */
  public static salesInvoicesConfirmBankTransferCreate(
id: number,
requestBody?: ConfirmBankTransfer,
): CancelablePromise<InvoiceDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/confirm-bank-transfer/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @returns InvoiceDetail 
   * @throws ApiError
   */
  public static salesInvoicesConfirmSettlementCreate(
id: number,
): CancelablePromise<InvoiceDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/confirm-settlement/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @param requestBody 
   * @returns InvoiceUrl 
   * @throws ApiError
   */
  public static salesInvoicesGetPaymentLinkCreate(
id: number,
requestBody: Invoice,
): CancelablePromise<InvoiceUrl> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/get-payment-link/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @param requestBody 
   * @returns PayResult 
   * @throws ApiError
   */
  public static salesInvoicesPayCreate(
id: number,
requestBody?: PayInvoice,
): CancelablePromise<PayResult> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/pay/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @returns InvoiceDetail 
   * @throws ApiError
   */
  public static salesInvoicesPreviewScheduledRetrieve(
id: number,
): CancelablePromise<InvoiceDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/invoices/{id}/preview-scheduled/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @param requestBody 
   * @returns InvoiceDetail 
   * @throws ApiError
   */
  public static salesInvoicesSendOverdueNoticeCreate(
id: number,
requestBody: InvoiceSendOverdue,
): CancelablePromise<InvoiceDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/send-overdue-notice/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @returns any 
   * @throws ApiError
   */
  public static salesInvoicesSendPaymentLinkCreate(
id: number,
): CancelablePromise<Record<string, any>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/send-payment-link/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @param requestBody 
   * @returns any 
   * @throws ApiError
   */
  public static salesInvoicesSendPdfCreate(
id: number,
requestBody?: SendCustomerNotification,
): CancelablePromise<Record<string, any>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/send-pdf/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this invoice.
   * @param requestBody 
   * @returns PayResult 
   * @throws ApiError
   */
  public static salesInvoicesStaffPayCreate(
id: number,
requestBody?: StaffPayInvoice,
): CancelablePromise<PayResult> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/invoices/{id}/staff-pay/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param author 
   * @param broker Filtrează facturile după ID-ul brokerului.
   * @param contractor Filtrează facturile după ID-ul entității contractante.
   * @param contractorType Filtrează facturile după tipul contractantului.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer Filtrează facturile după ID-ul clientului.
   * @param date 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludePaymentType Exclude facturile care au tipul de plată transmis.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param excludeStage Exclude facturile care au oricare dintre etapele transmise.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param excludeStatus Exclude facturile care au oricare dintre statusurile transmise.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param ids comma separated list
   * @param isMobile Filtrează facturile comenzilor create din aplicația mobilă (iOS sau Android).
   * @param isRegisteredUser Filtrează facturile pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param number 
   * @param order 
   * @param ordering Ordering fields: created, modified, date, order__product, series, number, _days_overdue
   * @param _package Filtrează facturile după pachetul casco.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @param paidDate 
   * @param paymentType * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează facturile după numărul poliței.
   * @param product Filtrează facturile după ID-urile produselor.
   * @param search Search fields: order__id, order__product__name, order__user__email, order__user__first_name, order__user__last_name, order__customer__email, order__customer__first_name, order__customer__last_name, order__contractor__as_person__email, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_person__identification_number, order__contractor__as_legalentity__identification_number, order__contractor__as_legalentity__name, order__policy__number, order__source, order__author__first_name, order__author__last_name, order__broker__name
   * @param series 
   * @param source Filtrează facturile după sursa comenzii. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param stage Filtrează facturile după etapă.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param status Filtrează facturile după status.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param type * `payment` - Payment
 * * `refund` - Refund
   * @param user Filtrează facturile după ID-ul utilizatorului.
   * @param validToGte Filtrează facturile cu data mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează facturile cu data mai mică sau egală decât valoarea transmisă.
   * @returns binary 
   * @throws ApiError
   */
  public static salesInvoicesCsvRetrieve(
author?: number,
broker?: number,
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
date?: string,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludePaymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
excludeStage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
excludeStatus?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
ids?: string,
isMobile?: boolean,
isRegisteredUser?: boolean,
number?: number,
order?: number,
ordering?: string,
_package?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
paidDate?: string,
paymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
policyNumber?: boolean,
product?: Array<number>,
search?: string,
series?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
stage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
status?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
type?: 'payment' | 'refund',
user?: number,
validToGte?: string,
validToLte?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/invoices/csv/',
      query: {
        'author': author,
        'broker': broker,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'date': date,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_payment_type': excludePaymentType,
        'exclude_stage': excludeStage,
        'exclude_status': excludeStatus,
        'ids': ids,
        'is_mobile': isMobile,
        'is_registered_user': isRegisteredUser,
        'number': number,
        'order': order,
        'ordering': ordering,
        'package': _package,
        'paid_date': paidDate,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'search': search,
        'series': series,
        'source': source,
        'stage': stage,
        'status': status,
        'type': type,
        'user': user,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
      },
    });
  }

  /**
   * @param author 
   * @param broker Filtrează facturile după ID-ul brokerului.
   * @param contractor Filtrează facturile după ID-ul entității contractante.
   * @param contractorType Filtrează facturile după tipul contractantului.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer Filtrează facturile după ID-ul clientului.
   * @param date 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludePaymentType Exclude facturile care au tipul de plată transmis.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param excludeStage Exclude facturile care au oricare dintre etapele transmise.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param excludeStatus Exclude facturile care au oricare dintre statusurile transmise.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param ids comma separated list
   * @param isMobile Filtrează facturile comenzilor create din aplicația mobilă (iOS sau Android).
   * @param isRegisteredUser Filtrează facturile pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param number 
   * @param order 
   * @param ordering Ordering fields: created, modified, date, order__product, series, number, _days_overdue
   * @param _package Filtrează facturile după pachetul casco.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @param paidDate 
   * @param paymentType * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează facturile după numărul poliței.
   * @param product Filtrează facturile după ID-urile produselor.
   * @param search Search fields: order__id, order__product__name, order__user__email, order__user__first_name, order__user__last_name, order__customer__email, order__customer__first_name, order__customer__last_name, order__contractor__as_person__email, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_person__identification_number, order__contractor__as_legalentity__identification_number, order__contractor__as_legalentity__name, order__policy__number, order__source, order__author__first_name, order__author__last_name, order__broker__name
   * @param series 
   * @param source Filtrează facturile după sursa comenzii. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param stage Filtrează facturile după etapă.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param status Filtrează facturile după status.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param type * `payment` - Payment
 * * `refund` - Refund
   * @param user Filtrează facturile după ID-ul utilizatorului.
   * @param validToGte Filtrează facturile cu data mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează facturile cu data mai mică sau egală decât valoarea transmisă.
   * @returns binary 
   * @throws ApiError
   */
  public static salesInvoicesDashboardCsvRetrieve(
author?: number,
broker?: number,
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
date?: string,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludePaymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
excludeStage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
excludeStatus?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
ids?: string,
isMobile?: boolean,
isRegisteredUser?: boolean,
number?: number,
order?: number,
ordering?: string,
_package?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
paidDate?: string,
paymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
policyNumber?: boolean,
product?: Array<number>,
search?: string,
series?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
stage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
status?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
type?: 'payment' | 'refund',
user?: number,
validToGte?: string,
validToLte?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/invoices/dashboard-csv/',
      query: {
        'author': author,
        'broker': broker,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'date': date,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_payment_type': excludePaymentType,
        'exclude_stage': excludeStage,
        'exclude_status': excludeStatus,
        'ids': ids,
        'is_mobile': isMobile,
        'is_registered_user': isRegisteredUser,
        'number': number,
        'order': order,
        'ordering': ordering,
        'package': _package,
        'paid_date': paidDate,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'search': search,
        'series': series,
        'source': source,
        'stage': stage,
        'status': status,
        'type': type,
        'user': user,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
      },
    });
  }

  /**
   * @param author 
   * @param broker Filtrează facturile după ID-ul brokerului.
   * @param contractor Filtrează facturile după ID-ul entității contractante.
   * @param contractorType Filtrează facturile după tipul contractantului.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer Filtrează facturile după ID-ul clientului.
   * @param date 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludePaymentType Exclude facturile care au tipul de plată transmis.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param excludeStage Exclude facturile care au oricare dintre etapele transmise.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param excludeStatus Exclude facturile care au oricare dintre statusurile transmise.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param ids comma separated list
   * @param isMobile Filtrează facturile comenzilor create din aplicația mobilă (iOS sau Android).
   * @param isRegisteredUser Filtrează facturile pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param number 
   * @param order 
   * @param ordering Ordering fields: created, modified, date, order__product, series, number, _days_overdue
   * @param _package Filtrează facturile după pachetul casco.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @param paidDate 
   * @param paymentType * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează facturile după numărul poliței.
   * @param product Filtrează facturile după ID-urile produselor.
   * @param search Search fields: order__id, order__product__name, order__user__email, order__user__first_name, order__user__last_name, order__customer__email, order__customer__first_name, order__customer__last_name, order__contractor__as_person__email, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_person__identification_number, order__contractor__as_legalentity__identification_number, order__contractor__as_legalentity__name, order__policy__number, order__source, order__author__first_name, order__author__last_name, order__broker__name
   * @param series 
   * @param source Filtrează facturile după sursa comenzii. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param stage Filtrează facturile după etapă.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param status Filtrează facturile după status.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param type * `payment` - Payment
 * * `refund` - Refund
   * @param user Filtrează facturile după ID-ul utilizatorului.
   * @param validToGte Filtrează facturile cu data mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează facturile cu data mai mică sau egală decât valoarea transmisă.
   * @returns binary 
   * @throws ApiError
   */
  public static salesInvoicesDashboardXlsxRetrieve(
author?: number,
broker?: number,
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
date?: string,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludePaymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
excludeStage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
excludeStatus?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
ids?: string,
isMobile?: boolean,
isRegisteredUser?: boolean,
number?: number,
order?: number,
ordering?: string,
_package?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
paidDate?: string,
paymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
policyNumber?: boolean,
product?: Array<number>,
search?: string,
series?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
stage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
status?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
type?: 'payment' | 'refund',
user?: number,
validToGte?: string,
validToLte?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/invoices/dashboard-xlsx/',
      query: {
        'author': author,
        'broker': broker,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'date': date,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_payment_type': excludePaymentType,
        'exclude_stage': excludeStage,
        'exclude_status': excludeStatus,
        'ids': ids,
        'is_mobile': isMobile,
        'is_registered_user': isRegisteredUser,
        'number': number,
        'order': order,
        'ordering': ordering,
        'package': _package,
        'paid_date': paidDate,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'search': search,
        'series': series,
        'source': source,
        'stage': stage,
        'status': status,
        'type': type,
        'user': user,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
      },
    });
  }

  /**
   * @param author 
   * @param broker Filtrează facturile după ID-ul brokerului.
   * @param contractor Filtrează facturile după ID-ul entității contractante.
   * @param contractorType Filtrează facturile după tipul contractantului.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer Filtrează facturile după ID-ul clientului.
   * @param date 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludePaymentType Exclude facturile care au tipul de plată transmis.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param excludeStage Exclude facturile care au oricare dintre etapele transmise.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param excludeStatus Exclude facturile care au oricare dintre statusurile transmise.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param ids comma separated list
   * @param isMobile Filtrează facturile comenzilor create din aplicația mobilă (iOS sau Android).
   * @param isRegisteredUser Filtrează facturile pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param number 
   * @param order 
   * @param ordering Ordering fields: created, modified, date, order__product, series, number, _days_overdue
   * @param _package Filtrează facturile după pachetul casco.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @param paidDate 
   * @param paymentType * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează facturile după numărul poliței.
   * @param product Filtrează facturile după ID-urile produselor.
   * @param search Search fields: order__id, order__product__name, order__user__email, order__user__first_name, order__user__last_name, order__customer__email, order__customer__first_name, order__customer__last_name, order__contractor__as_person__email, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_person__identification_number, order__contractor__as_legalentity__identification_number, order__contractor__as_legalentity__name, order__policy__number, order__source, order__author__first_name, order__author__last_name, order__broker__name
   * @param series 
   * @param source Filtrează facturile după sursa comenzii. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param stage Filtrează facturile după etapă.
 *
 * * `initial` - Initial
 * * `full` - Full
 * * `installment` - Installment
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param status Filtrează facturile după status.
 *
 * * `ready` - Ready
 * * `in_progress` - In Progress
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `paid` - Paid
 * * `pending_settlement` - Pending Settlement
 * * `error` - Error
 * * `refunded` - Refunded
 * * `canceled` - Canceled
 * * `scheduled` - Scheduled
 * * `overdue` - Overdue
   * @param type * `payment` - Payment
 * * `refund` - Refund
   * @param user Filtrează facturile după ID-ul utilizatorului.
   * @param validToGte Filtrează facturile cu data mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează facturile cu data mai mică sau egală decât valoarea transmisă.
   * @returns binary 
   * @throws ApiError
   */
  public static salesInvoicesXlsxRetrieve(
author?: number,
broker?: number,
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
date?: string,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludePaymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
excludeStage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
excludeStatus?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
ids?: string,
isMobile?: boolean,
isRegisteredUser?: boolean,
number?: number,
order?: number,
ordering?: string,
_package?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
paidDate?: string,
paymentType?: 'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos',
policyNumber?: boolean,
product?: Array<number>,
search?: string,
series?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
stage?: Array<'cancel' | 'full' | 'initial' | 'installment' | 'modification'>,
status?: Array<'authorized' | 'canceled' | 'error' | 'in_progress' | 'overdue' | 'paid' | 'pending' | 'pending_settlement' | 'ready' | 'refunded' | 'scheduled'>,
type?: 'payment' | 'refund',
user?: number,
validToGte?: string,
validToLte?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/invoices/xlsx/',
      query: {
        'author': author,
        'broker': broker,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'date': date,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_payment_type': excludePaymentType,
        'exclude_stage': excludeStage,
        'exclude_status': excludeStatus,
        'ids': ids,
        'is_mobile': isMobile,
        'is_registered_user': isRegisteredUser,
        'number': number,
        'order': order,
        'ordering': ordering,
        'package': _package,
        'paid_date': paidDate,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'search': search,
        'series': series,
        'source': source,
        'stage': stage,
        'status': status,
        'type': type,
        'user': user,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
      },
    });
  }

  /**
   * @param order 
   * @param ordering Ordering fields: created
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedOrderOfferList 
   * @throws ApiError
   */
  public static salesOffersList(
order?: number,
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedOrderOfferList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/offers/',
      query: {
        'order': order,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this offer.
   * @returns OrderOfferDetail 
   * @throws ApiError
   */
  public static salesOffersRetrieve(
id: number,
): CancelablePromise<OrderOfferDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/offers/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param agent Filtrează comenzile după ID-urile agenților.
   * @param amountGte Filtrează comenzile cu suma mai mare sau egală decât valoarea transmisă.
   * @param amountLte Filtrează comenzile cu suma mai mică sau egală decât valoarea transmisă.
   * @param author 
   * @param broker 
   * @param contractType Filtrează comenzile de accidente după tipul contractului.
 *
 * * `individual` - Individual
 * * `familial` - Familial
 * * `collective` - Collective
   * @param contractor 
   * @param contractorType Filtrează comenzile după tipul contractantului: persoană fizică sau entitate juridică.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer 
   * @param customerIsNull 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludeStatus Exclude comenzile care au oricare dintre statusurile transmise.
 *
 * * `draft` - Draft
 * * `checkout` - Checkout
 * * `in_progress` - In Progress
 * * `paid` - Paid
 * * `partial_paid` - Partial Paid
 * * `error` - Error
 * * `critical` - Critical
 * * `canceled` - Canceled
 * * `completed` - Completed
 * * `expired` - Expired
 * * `pending_refund` - Pending Refund
 * * `pending` - Pending
 * * `refunded` - Refunded
 * * `overdue` - Overdue
 * * `terminated` - Terminated
 * * `refused` - Refused
 * * `submitted` - Submitted
 * * `data_entry` - Data Entry
 * * `inspection` - Inspection
 * * `offer_ready` - Offer Ready
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `in_processing` - In Processing
   * @param ids comma separated list
   * @param insured Filtrează comenzile după ID-urile entităților asigurate.
   * @param isMobile Filtrează comenzile create din aplicația mobilă (iOS sau Android).
   * @param isModified Filtrează comenzile în funcție de existența unor comenzi de modificare ulterioare.
   * @param isRegisteredUser Filtrează comenzile create pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param ordering Ordering fields: created, modified, date, product, user__first_name, user__last_name, amount, author_full_name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param paymentType Filtrează comenzile după tipul de plată al facturii.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează comenzile după numărul poliței, potrivire parțială.
   * @param product 
   * @param productCode Filtrează comenzile după codul exact al produsului.
   * @param search Search fields: id, policy__number, parent__policy__number, product__name, user__email, user__first_name, user__last_name, customer__email, customer__first_name, customer__last_name, contractor__as_person__email, contractor__as_person__first_name, contractor__as_person__last_name, contractor__as_person__identification_number, contractor__as_legalentity__identification_number, contractor__as_legalentity__name, insured__entity__as_person__email, insured__entity__as_person__first_name, insured__entity__as_person__last_name, insured__entity__as_person__identification_number, insured__entity__as_vehicle__certificate_number, insured__entity__as_vehicle__registration_number
   * @param source Filtrează comenzile după sursa din care au fost create. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată în `source`, deci nu comparați câmpul din răspuns cu ea. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param status Filtrează comenzile după status.
 *
 * * `draft` - Draft
 * * `checkout` - Checkout
 * * `in_progress` - In Progress
 * * `paid` - Paid
 * * `partial_paid` - Partial Paid
 * * `error` - Error
 * * `critical` - Critical
 * * `canceled` - Canceled
 * * `completed` - Completed
 * * `expired` - Expired
 * * `pending_refund` - Pending Refund
 * * `pending` - Pending
 * * `refunded` - Refunded
 * * `overdue` - Overdue
 * * `terminated` - Terminated
 * * `refused` - Refused
 * * `submitted` - Submitted
 * * `data_entry` - Data Entry
 * * `inspection` - Inspection
 * * `offer_ready` - Offer Ready
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `in_processing` - In Processing
   * @param team SAAS id
   * @param type Filtrează comenzile după tipul comenzii.
 *
 * * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param user 
   * @param validFromGte Filtrează comenzile cu data de început a valabilității mai mare sau egală decât valoarea transmisă.
   * @param validToGte Filtrează comenzile cu data de sfârșit a valabilității mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează comenzile cu data de sfârșit a valabilității mai mică sau egală decât valoarea transmisă.
   * @param variation Product sub-type (see product.variation). CASCO only: mini/standard/comfort/fifty_fifty.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @returns PaginatedOrderListList 
   * @throws ApiError
   */
  public static salesOrdersList(
agent?: Array<number>,
amountGte?: number,
amountLte?: number,
author?: number,
broker?: number,
contractType?: 'collective' | 'familial' | 'individual',
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
customerIsNull?: boolean,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludeStatus?: Array<'approved' | 'canceled' | 'checkout' | 'completed' | 'critical' | 'data_entry' | 'draft' | 'error' | 'expired' | 'in_processing' | 'in_progress' | 'inspection' | 'offer_ready' | 'overdue' | 'paid' | 'partial_paid' | 'pending' | 'pending_refund' | 'refunded' | 'refused' | 'rejected' | 'submitted' | 'terminated'>,
ids?: string,
insured?: Array<number>,
isMobile?: boolean,
isModified?: boolean,
isRegisteredUser?: boolean,
ordering?: string,
page?: number,
pageSize?: number,
paymentType?: Array<'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos'>,
policyNumber?: string,
product?: number,
productCode?: string,
search?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
status?: Array<'approved' | 'canceled' | 'checkout' | 'completed' | 'critical' | 'data_entry' | 'draft' | 'error' | 'expired' | 'in_processing' | 'in_progress' | 'inspection' | 'offer_ready' | 'overdue' | 'paid' | 'partial_paid' | 'pending' | 'pending_refund' | 'refunded' | 'refused' | 'rejected' | 'submitted' | 'terminated'>,
team?: string,
type?: Array<'cancel' | 'modification' | 'order'>,
user?: number,
validFromGte?: string,
validToGte?: string,
validToLte?: string,
variation?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
): CancelablePromise<PaginatedOrderListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders/',
      query: {
        'agent': agent,
        'amount_gte': amountGte,
        'amount_lte': amountLte,
        'author': author,
        'broker': broker,
        'contract_type': contractType,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'customer_is_null': customerIsNull,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_status': excludeStatus,
        'ids': ids,
        'insured': insured,
        'is_mobile': isMobile,
        'is_modified': isModified,
        'is_registered_user': isRegisteredUser,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'product_code': productCode,
        'search': search,
        'source': source,
        'status': status,
        'team': team,
        'type': type,
        'user': user,
        'valid_from_gte': validFromGte,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
        'variation': variation,
      },
    });
  }

  /**
   * @param order 
   * @param ordering Ordering fields: media__name, created
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedOrderMediaDetailList 
   * @throws ApiError
   */
  public static salesOrdersMediaList(
order?: number,
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedOrderMediaDetailList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders-media/',
      query: {
        'order': order,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param formData 
   * @returns OrderMediaCreate 
   * @throws ApiError
   */
  public static salesOrdersMediaCreate(
formData: OrderMediaCreate,
): CancelablePromise<OrderMediaCreate> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders-media/',
      formData: formData,
      mediaType: 'multipart/form-data',
    });
  }

  /**
   * @param id A unique integer value identifying this order media.
   * @returns OrderMediaDetail 
   * @throws ApiError
   */
  public static salesOrdersMediaRetrieve(
id: number,
): CancelablePromise<OrderMediaDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders-media/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this order media.
   * @returns void 
   * @throws ApiError
   */
  public static salesOrdersMediaDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/sales/orders-media/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @returns OrderDetail 
   * @throws ApiError
   */
  public static salesOrdersRetrieve(
id: number,
): CancelablePromise<OrderDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns OrderDetail 
   * @throws ApiError
   */
  public static salesOrdersPartialUpdate(
id: number,
requestBody?: PatchedOrderEditRequest,
): CancelablePromise<OrderDetail> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/sales/orders/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns OrderApproveResponse 
   * @throws ApiError
   */
  public static salesOrdersApproveCreate(
id: number,
requestBody?: OrderApprove,
): CancelablePromise<OrderApproveResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/approve/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersAssignCreate(
id: number,
requestBody: OrderAssign,
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/assign/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param refundDestination * `refund` - Refund
 * * `credit` - Credit
   * @param validFrom effective cancellation date
   * @param paymentType * `cash` - Cash
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `broker_settlement` - Broker Settlement
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersCalculateCancelRetrieve(
id: number,
refundDestination: 'refund' | 'credit',
validFrom: string,
paymentType: 'cash' | 'cash_mia' | 'pos' | 'bank_transfer' | 'credit' | 'broker_settlement' = 'cash',
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders/{id}/calculate-cancel/',
      path: {
        'id': id,
      },
      query: {
        'payment_type': paymentType,
        'refund_destination': refundDestination,
        'valid_from': validFrom,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersCancelCreate(
id: number,
requestBody: OrderCancel,
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/cancel/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Create invoice(s)
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns InvoiceDetail 
   * @throws ApiError
   */
  public static salesOrdersCheckoutCreate(
id: number,
requestBody?: OrderCheckout,
): CancelablePromise<InvoiceDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/checkout/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersCompleteRefundCreate(
id: number,
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/complete-refund/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersDownloadDocumentsCreate(
id: number,
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/download-documents/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns ModificationPrice 
   * @throws ApiError
   */
  public static salesOrdersModificationPriceCreate(
id: number,
requestBody?: OrderModify,
): CancelablePromise<ModificationPrice> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/modification-price/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Change of contract
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersModifyCreate(
id: number,
requestBody?: OrderModify,
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/modify/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns Price 
   * @throws ApiError
   */
  public static salesOrdersPreviewPriceAdjustmentCreate(
id: number,
requestBody?: OrderApprove,
): CancelablePromise<Price> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/preview-price-adjustment/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Re apply failed but paid order.
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersReApplyCreate(
id: number,
requestBody: Order,
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/re-apply/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersRefuseCreate(
id: number,
requestBody: OrderReason,
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/refuse/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns Order 
   * @throws ApiError
   */
  public static salesOrdersRejectCreate(
id: number,
requestBody: OrderReason,
): CancelablePromise<Order> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/reject/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @param requestBody 
   * @returns EnqueuedOffer 
   * @throws ApiError
   */
  public static salesOrdersSendOfferCreate(
id: number,
requestBody?: SendOfferNotification,
): CancelablePromise<EnqueuedOffer> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/orders/{id}/send-offer/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this order.
   * @returns OrderUserDetail 
   * @throws ApiError
   */
  public static salesOrdersUserRetrieveRetrieve(
id: number,
): CancelablePromise<OrderUserDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders/{id}/user-retrieve/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param agent Filtrează comenzile după ID-urile agenților.
   * @param amountGte Filtrează comenzile cu suma mai mare sau egală decât valoarea transmisă.
   * @param amountLte Filtrează comenzile cu suma mai mică sau egală decât valoarea transmisă.
   * @param author 
   * @param broker 
   * @param contractType Filtrează comenzile de accidente după tipul contractului.
 *
 * * `individual` - Individual
 * * `familial` - Familial
 * * `collective` - Collective
   * @param contractor 
   * @param contractorType Filtrează comenzile după tipul contractantului: persoană fizică sau entitate juridică.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer 
   * @param customerIsNull 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludeStatus Exclude comenzile care au oricare dintre statusurile transmise.
 *
 * * `draft` - Draft
 * * `checkout` - Checkout
 * * `in_progress` - In Progress
 * * `paid` - Paid
 * * `partial_paid` - Partial Paid
 * * `error` - Error
 * * `critical` - Critical
 * * `canceled` - Canceled
 * * `completed` - Completed
 * * `expired` - Expired
 * * `pending_refund` - Pending Refund
 * * `pending` - Pending
 * * `refunded` - Refunded
 * * `overdue` - Overdue
 * * `terminated` - Terminated
 * * `refused` - Refused
 * * `submitted` - Submitted
 * * `data_entry` - Data Entry
 * * `inspection` - Inspection
 * * `offer_ready` - Offer Ready
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `in_processing` - In Processing
   * @param ids comma separated list
   * @param insured Filtrează comenzile după ID-urile entităților asigurate.
   * @param isMobile Filtrează comenzile create din aplicația mobilă (iOS sau Android).
   * @param isModified Filtrează comenzile în funcție de existența unor comenzi de modificare ulterioare.
   * @param isRegisteredUser Filtrează comenzile create pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param ordering Ordering fields: created, modified, date, product, user__first_name, user__last_name, amount, author_full_name
   * @param paymentType Filtrează comenzile după tipul de plată al facturii.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează comenzile după numărul poliței, potrivire parțială.
   * @param product 
   * @param productCode Filtrează comenzile după codul exact al produsului.
   * @param search Search fields: id, policy__number, parent__policy__number, product__name, user__email, user__first_name, user__last_name, customer__email, customer__first_name, customer__last_name, contractor__as_person__email, contractor__as_person__first_name, contractor__as_person__last_name, contractor__as_person__identification_number, contractor__as_legalentity__identification_number, contractor__as_legalentity__name, insured__entity__as_person__email, insured__entity__as_person__first_name, insured__entity__as_person__last_name, insured__entity__as_person__identification_number, insured__entity__as_vehicle__certificate_number, insured__entity__as_vehicle__registration_number
   * @param source Filtrează comenzile după sursa din care au fost create. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată în `source`, deci nu comparați câmpul din răspuns cu ea. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param status Filtrează comenzile după status.
 *
 * * `draft` - Draft
 * * `checkout` - Checkout
 * * `in_progress` - In Progress
 * * `paid` - Paid
 * * `partial_paid` - Partial Paid
 * * `error` - Error
 * * `critical` - Critical
 * * `canceled` - Canceled
 * * `completed` - Completed
 * * `expired` - Expired
 * * `pending_refund` - Pending Refund
 * * `pending` - Pending
 * * `refunded` - Refunded
 * * `overdue` - Overdue
 * * `terminated` - Terminated
 * * `refused` - Refused
 * * `submitted` - Submitted
 * * `data_entry` - Data Entry
 * * `inspection` - Inspection
 * * `offer_ready` - Offer Ready
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `in_processing` - In Processing
   * @param team SAAS id
   * @param type Filtrează comenzile după tipul comenzii.
 *
 * * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param user 
   * @param validFromGte Filtrează comenzile cu data de început a valabilității mai mare sau egală decât valoarea transmisă.
   * @param validToGte Filtrează comenzile cu data de sfârșit a valabilității mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează comenzile cu data de sfârșit a valabilității mai mică sau egală decât valoarea transmisă.
   * @param variation Product sub-type (see product.variation). CASCO only: mini/standard/comfort/fifty_fifty.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @returns binary 
   * @throws ApiError
   */
  public static salesOrdersCsvRetrieve(
agent?: Array<number>,
amountGte?: number,
amountLte?: number,
author?: number,
broker?: number,
contractType?: 'collective' | 'familial' | 'individual',
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
customerIsNull?: boolean,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludeStatus?: Array<'approved' | 'canceled' | 'checkout' | 'completed' | 'critical' | 'data_entry' | 'draft' | 'error' | 'expired' | 'in_processing' | 'in_progress' | 'inspection' | 'offer_ready' | 'overdue' | 'paid' | 'partial_paid' | 'pending' | 'pending_refund' | 'refunded' | 'refused' | 'rejected' | 'submitted' | 'terminated'>,
ids?: string,
insured?: Array<number>,
isMobile?: boolean,
isModified?: boolean,
isRegisteredUser?: boolean,
ordering?: string,
paymentType?: Array<'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos'>,
policyNumber?: string,
product?: number,
productCode?: string,
search?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
status?: Array<'approved' | 'canceled' | 'checkout' | 'completed' | 'critical' | 'data_entry' | 'draft' | 'error' | 'expired' | 'in_processing' | 'in_progress' | 'inspection' | 'offer_ready' | 'overdue' | 'paid' | 'partial_paid' | 'pending' | 'pending_refund' | 'refunded' | 'refused' | 'rejected' | 'submitted' | 'terminated'>,
team?: string,
type?: Array<'cancel' | 'modification' | 'order'>,
user?: number,
validFromGte?: string,
validToGte?: string,
validToLte?: string,
variation?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders/csv/',
      query: {
        'agent': agent,
        'amount_gte': amountGte,
        'amount_lte': amountLte,
        'author': author,
        'broker': broker,
        'contract_type': contractType,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'customer_is_null': customerIsNull,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_status': excludeStatus,
        'ids': ids,
        'insured': insured,
        'is_mobile': isMobile,
        'is_modified': isModified,
        'is_registered_user': isRegisteredUser,
        'ordering': ordering,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'product_code': productCode,
        'search': search,
        'source': source,
        'status': status,
        'team': team,
        'type': type,
        'user': user,
        'valid_from_gte': validFromGte,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
        'variation': variation,
      },
    });
  }

  /**
   * @param agent Filtrează comenzile după ID-urile agenților.
   * @param amountGte Filtrează comenzile cu suma mai mare sau egală decât valoarea transmisă.
   * @param amountLte Filtrează comenzile cu suma mai mică sau egală decât valoarea transmisă.
   * @param author 
   * @param broker 
   * @param contractType Filtrează comenzile de accidente după tipul contractului.
 *
 * * `individual` - Individual
 * * `familial` - Familial
 * * `collective` - Collective
   * @param contractor 
   * @param contractorType Filtrează comenzile după tipul contractantului: persoană fizică sau entitate juridică.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer 
   * @param customerIsNull 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludeStatus Exclude comenzile care au oricare dintre statusurile transmise.
 *
 * * `draft` - Draft
 * * `checkout` - Checkout
 * * `in_progress` - In Progress
 * * `paid` - Paid
 * * `partial_paid` - Partial Paid
 * * `error` - Error
 * * `critical` - Critical
 * * `canceled` - Canceled
 * * `completed` - Completed
 * * `expired` - Expired
 * * `pending_refund` - Pending Refund
 * * `pending` - Pending
 * * `refunded` - Refunded
 * * `overdue` - Overdue
 * * `terminated` - Terminated
 * * `refused` - Refused
 * * `submitted` - Submitted
 * * `data_entry` - Data Entry
 * * `inspection` - Inspection
 * * `offer_ready` - Offer Ready
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `in_processing` - In Processing
   * @param ids comma separated list
   * @param insured Filtrează comenzile după ID-urile entităților asigurate.
   * @param isMobile Filtrează comenzile create din aplicația mobilă (iOS sau Android).
   * @param isModified Filtrează comenzile în funcție de existența unor comenzi de modificare ulterioare.
   * @param isRegisteredUser Filtrează comenzile create pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param ordering Ordering fields: created, modified, date, product, user__first_name, user__last_name, amount, author_full_name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param paymentType Filtrează comenzile după tipul de plată al facturii.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează comenzile după numărul poliței, potrivire parțială.
   * @param product 
   * @param productCode Filtrează comenzile după codul exact al produsului.
   * @param search Search fields: id, policy__number, parent__policy__number, product__name, user__email, user__first_name, user__last_name, customer__email, customer__first_name, customer__last_name, contractor__as_person__email, contractor__as_person__first_name, contractor__as_person__last_name, contractor__as_person__identification_number, contractor__as_legalentity__identification_number, contractor__as_legalentity__name, insured__entity__as_person__email, insured__entity__as_person__first_name, insured__entity__as_person__last_name, insured__entity__as_person__identification_number, insured__entity__as_vehicle__certificate_number, insured__entity__as_vehicle__registration_number
   * @param source Filtrează comenzile după sursa din care au fost create. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată în `source`, deci nu comparați câmpul din răspuns cu ea. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param status Filtrează comenzile după status.
 *
 * * `draft` - Draft
 * * `checkout` - Checkout
 * * `in_progress` - In Progress
 * * `paid` - Paid
 * * `partial_paid` - Partial Paid
 * * `error` - Error
 * * `critical` - Critical
 * * `canceled` - Canceled
 * * `completed` - Completed
 * * `expired` - Expired
 * * `pending_refund` - Pending Refund
 * * `pending` - Pending
 * * `refunded` - Refunded
 * * `overdue` - Overdue
 * * `terminated` - Terminated
 * * `refused` - Refused
 * * `submitted` - Submitted
 * * `data_entry` - Data Entry
 * * `inspection` - Inspection
 * * `offer_ready` - Offer Ready
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `in_processing` - In Processing
   * @param team SAAS id
   * @param type Filtrează comenzile după tipul comenzii.
 *
 * * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param user 
   * @param validFromGte Filtrează comenzile cu data de început a valabilității mai mare sau egală decât valoarea transmisă.
   * @param validToGte Filtrează comenzile cu data de sfârșit a valabilității mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează comenzile cu data de sfârșit a valabilității mai mică sau egală decât valoarea transmisă.
   * @param variation Product sub-type (see product.variation). CASCO only: mini/standard/comfort/fifty_fifty.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @returns PaginatedOrderUserListList 
   * @throws ApiError
   */
  public static salesOrdersUserListList(
agent?: Array<number>,
amountGte?: number,
amountLte?: number,
author?: number,
broker?: number,
contractType?: 'collective' | 'familial' | 'individual',
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
customerIsNull?: boolean,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludeStatus?: Array<'approved' | 'canceled' | 'checkout' | 'completed' | 'critical' | 'data_entry' | 'draft' | 'error' | 'expired' | 'in_processing' | 'in_progress' | 'inspection' | 'offer_ready' | 'overdue' | 'paid' | 'partial_paid' | 'pending' | 'pending_refund' | 'refunded' | 'refused' | 'rejected' | 'submitted' | 'terminated'>,
ids?: string,
insured?: Array<number>,
isMobile?: boolean,
isModified?: boolean,
isRegisteredUser?: boolean,
ordering?: string,
page?: number,
pageSize?: number,
paymentType?: Array<'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos'>,
policyNumber?: string,
product?: number,
productCode?: string,
search?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
status?: Array<'approved' | 'canceled' | 'checkout' | 'completed' | 'critical' | 'data_entry' | 'draft' | 'error' | 'expired' | 'in_processing' | 'in_progress' | 'inspection' | 'offer_ready' | 'overdue' | 'paid' | 'partial_paid' | 'pending' | 'pending_refund' | 'refunded' | 'refused' | 'rejected' | 'submitted' | 'terminated'>,
team?: string,
type?: Array<'cancel' | 'modification' | 'order'>,
user?: number,
validFromGte?: string,
validToGte?: string,
validToLte?: string,
variation?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
): CancelablePromise<PaginatedOrderUserListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders/user-list/',
      query: {
        'agent': agent,
        'amount_gte': amountGte,
        'amount_lte': amountLte,
        'author': author,
        'broker': broker,
        'contract_type': contractType,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'customer_is_null': customerIsNull,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_status': excludeStatus,
        'ids': ids,
        'insured': insured,
        'is_mobile': isMobile,
        'is_modified': isModified,
        'is_registered_user': isRegisteredUser,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'product_code': productCode,
        'search': search,
        'source': source,
        'status': status,
        'team': team,
        'type': type,
        'user': user,
        'valid_from_gte': validFromGte,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
        'variation': variation,
      },
    });
  }

  /**
   * @param agent Filtrează comenzile după ID-urile agenților.
   * @param amountGte Filtrează comenzile cu suma mai mare sau egală decât valoarea transmisă.
   * @param amountLte Filtrează comenzile cu suma mai mică sau egală decât valoarea transmisă.
   * @param author 
   * @param broker 
   * @param contractType Filtrează comenzile de accidente după tipul contractului.
 *
 * * `individual` - Individual
 * * `familial` - Familial
 * * `collective` - Collective
   * @param contractor 
   * @param contractorType Filtrează comenzile după tipul contractantului: persoană fizică sau entitate juridică.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
   * @param customer 
   * @param customerIsNull 
   * @param dateGte 
   * @param dateLte 
   * @param excludeIds comma separated list
   * @param excludeStatus Exclude comenzile care au oricare dintre statusurile transmise.
 *
 * * `draft` - Draft
 * * `checkout` - Checkout
 * * `in_progress` - In Progress
 * * `paid` - Paid
 * * `partial_paid` - Partial Paid
 * * `error` - Error
 * * `critical` - Critical
 * * `canceled` - Canceled
 * * `completed` - Completed
 * * `expired` - Expired
 * * `pending_refund` - Pending Refund
 * * `pending` - Pending
 * * `refunded` - Refunded
 * * `overdue` - Overdue
 * * `terminated` - Terminated
 * * `refused` - Refused
 * * `submitted` - Submitted
 * * `data_entry` - Data Entry
 * * `inspection` - Inspection
 * * `offer_ready` - Offer Ready
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `in_processing` - In Processing
   * @param ids comma separated list
   * @param insured Filtrează comenzile după ID-urile entităților asigurate.
   * @param isMobile Filtrează comenzile create din aplicația mobilă (iOS sau Android).
   * @param isModified Filtrează comenzile în funcție de existența unor comenzi de modificare ulterioare.
   * @param isRegisteredUser Filtrează comenzile create pentru utilizatori înregistrați sau pentru clienți anonimi.
   * @param ordering Ordering fields: created, modified, date, product, user__first_name, user__last_name, amount, author_full_name
   * @param paymentType Filtrează comenzile după tipul de plată al facturii.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param policyNumber Filtrează comenzile după numărul poliței, potrivire parțială.
   * @param product 
   * @param productCode Filtrează comenzile după codul exact al produsului.
   * @param search Search fields: id, policy__number, parent__policy__number, product__name, user__email, user__first_name, user__last_name, customer__email, customer__first_name, customer__last_name, contractor__as_person__email, contractor__as_person__first_name, contractor__as_person__last_name, contractor__as_person__identification_number, contractor__as_legalentity__identification_number, contractor__as_legalentity__name, insured__entity__as_person__email, insured__entity__as_person__first_name, insured__entity__as_person__last_name, insured__entity__as_person__identification_number, insured__entity__as_vehicle__certificate_number, insured__entity__as_vehicle__registration_number
   * @param source Filtrează comenzile după sursa din care au fost create. `mobile` este un alias de filtrare pentru `ios` + `android` — nu este o valoare stocată în `source`, deci nu comparați câmpul din răspuns cu ea. Valorile se transmit repetat: `?source=mobile&source=agent`.
 *
 * * `website` - Website
 * * `agent` - Agent
 * * `broker` - Broker
 * * `ios` - iOS
 * * `android` - Android
 * * `mobile` - Mobile (iOS + Android)
   * @param status Filtrează comenzile după status.
 *
 * * `draft` - Draft
 * * `checkout` - Checkout
 * * `in_progress` - In Progress
 * * `paid` - Paid
 * * `partial_paid` - Partial Paid
 * * `error` - Error
 * * `critical` - Critical
 * * `canceled` - Canceled
 * * `completed` - Completed
 * * `expired` - Expired
 * * `pending_refund` - Pending Refund
 * * `pending` - Pending
 * * `refunded` - Refunded
 * * `overdue` - Overdue
 * * `terminated` - Terminated
 * * `refused` - Refused
 * * `submitted` - Submitted
 * * `data_entry` - Data Entry
 * * `inspection` - Inspection
 * * `offer_ready` - Offer Ready
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `in_processing` - In Processing
   * @param team SAAS id
   * @param type Filtrează comenzile după tipul comenzii.
 *
 * * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param user 
   * @param validFromGte Filtrează comenzile cu data de început a valabilității mai mare sau egală decât valoarea transmisă.
   * @param validToGte Filtrează comenzile cu data de sfârșit a valabilității mai mare sau egală decât valoarea transmisă.
   * @param validToLte Filtrează comenzile cu data de sfârșit a valabilității mai mică sau egală decât valoarea transmisă.
   * @param variation Product sub-type (see product.variation). CASCO only: mini/standard/comfort/fifty_fifty.
 *
 * * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @returns binary 
   * @throws ApiError
   */
  public static salesOrdersXlsxRetrieve(
agent?: Array<number>,
amountGte?: number,
amountLte?: number,
author?: number,
broker?: number,
contractType?: 'collective' | 'familial' | 'individual',
contractor?: number,
contractorType?: 'legalentity' | 'person',
customer?: number,
customerIsNull?: boolean,
dateGte?: string,
dateLte?: string,
excludeIds?: string,
excludeStatus?: Array<'approved' | 'canceled' | 'checkout' | 'completed' | 'critical' | 'data_entry' | 'draft' | 'error' | 'expired' | 'in_processing' | 'in_progress' | 'inspection' | 'offer_ready' | 'overdue' | 'paid' | 'partial_paid' | 'pending' | 'pending_refund' | 'refunded' | 'refused' | 'rejected' | 'submitted' | 'terminated'>,
ids?: string,
insured?: Array<number>,
isMobile?: boolean,
isModified?: boolean,
isRegisteredUser?: boolean,
ordering?: string,
paymentType?: Array<'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos'>,
policyNumber?: string,
product?: number,
productCode?: string,
search?: string,
source?: Array<'agent' | 'android' | 'broker' | 'ios' | 'mobile' | 'website'>,
status?: Array<'approved' | 'canceled' | 'checkout' | 'completed' | 'critical' | 'data_entry' | 'draft' | 'error' | 'expired' | 'in_processing' | 'in_progress' | 'inspection' | 'offer_ready' | 'overdue' | 'paid' | 'partial_paid' | 'pending' | 'pending_refund' | 'refunded' | 'refused' | 'rejected' | 'submitted' | 'terminated'>,
team?: string,
type?: Array<'cancel' | 'modification' | 'order'>,
user?: number,
validFromGte?: string,
validToGte?: string,
validToLte?: string,
variation?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/orders/xlsx/',
      query: {
        'agent': agent,
        'amount_gte': amountGte,
        'amount_lte': amountLte,
        'author': author,
        'broker': broker,
        'contract_type': contractType,
        'contractor': contractor,
        'contractor_type': contractorType,
        'customer': customer,
        'customer_is_null': customerIsNull,
        'date_gte': dateGte,
        'date_lte': dateLte,
        'exclude_ids': excludeIds,
        'exclude_status': excludeStatus,
        'ids': ids,
        'insured': insured,
        'is_mobile': isMobile,
        'is_modified': isModified,
        'is_registered_user': isRegisteredUser,
        'ordering': ordering,
        'payment_type': paymentType,
        'policy_number': policyNumber,
        'product': product,
        'product_code': productCode,
        'search': search,
        'source': source,
        'status': status,
        'team': team,
        'type': type,
        'user': user,
        'valid_from_gte': validFromGte,
        'valid_to_gte': validToGte,
        'valid_to_lte': validToLte,
        'variation': variation,
      },
    });
  }

  /**
   * @param availableToBrokers Filtrează produsele disponibile pentru brokeri.
   * @param code Filtrează produsele după codul produsului.
 *
 * * `rca` - RCA
 * * `green_card` - Carte Verde
 * * `medical_travel` - Asigurarea medicală
 * * `accidents` - Asigurarea de accidente
 * * `casco` - Casco
 * * `property` - Asigurarea bunurilor
 * * `cmr` - Asigurarea CMR
 * * `financial` - Asigurarea riscurilor financiare de călătorie
 * * `guarantees` - Asigurări de garanții
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified, name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, description
   * @returns PaginatedProductListList 
   * @throws ApiError
   */
  public static salesProductsList(
availableToBrokers?: boolean,
code?: Array<'accidents' | 'casco' | 'cmr' | 'financial' | 'green_card' | 'guarantees' | 'medical_travel' | 'property' | 'rca'>,
excludeIds?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedProductListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/products/',
      query: {
        'available_to_brokers': availableToBrokers,
        'code': code,
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * Create a public order for a product.  CHECKOUT or SUBMITTED.
   * @param code Product code
   * @param xPlatform Mobile client platform. When present, the order source becomes "ios"/"android" instead of "website".
   * @param requestBody 
   * @returns OrderProduct 
   * @throws ApiError
   */
  public static salesProductsOrderCreate(
code: string,
xPlatform?: 'android' | 'ios',
requestBody?: OrderRequest,
): CancelablePromise<OrderProduct> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/products/{code}/order/',
      path: {
        'code': code,
      },
      headers: {
        'X-Platform': xPlatform,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param code Product code
   * @param xPlatform Mobile client platform. When present, the order source becomes "ios"/"android" instead of "website".
   * @param requestBody 
   * @returns PriceResponse 
   * @throws ApiError
   */
  public static salesProductsPriceCreate(
code: string,
xPlatform?: 'android' | 'ios',
requestBody?: PriceRequest,
): CancelablePromise<PriceResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/products/{code}/price/',
      path: {
        'code': code,
      },
      headers: {
        'X-Platform': xPlatform,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this product.
   * @returns ProductDetail 
   * @throws ApiError
   */
  public static salesProductsRetrieve(
id: number,
): CancelablePromise<ProductDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/products/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this product.
   * @param requestBody 
   * @returns Product 
   * @throws ApiError
   */
  public static salesProductsPartialUpdate(
id: number,
requestBody?: PatchedProduct,
): CancelablePromise<Product> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/sales/products/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @deprecated
   * Deprecated. Оформление заказа одним вызовом сохранено только для обратной совместимости и будет отключено 30.11.2026. Актуальный порядок: `POST /sales/products/{code}/price/` (с `session`) -> `PATCH /sales/orders/{id}/` -> `POST /sales/orders/{id}/checkout/`.
   * @param requestBody 
   * @returns OrderProduct 
   * @throws ApiError
   */
  public static salesProductsAccidentsOrderBrokerCreate(
requestBody: StaffOrderAccidents,
): CancelablePromise<OrderProduct> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/products/accidents/order-broker/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @deprecated
   * Deprecated. Оформление заказа одним вызовом сохранено только для обратной совместимости и будет отключено 30.11.2026. Актуальный порядок: `POST /sales/products/{code}/price/` (с `session`) -> `PATCH /sales/orders/{id}/` -> `POST /sales/orders/{id}/checkout/`.
   * @param requestBody 
   * @returns OrderProduct 
   * @throws ApiError
   */
  public static salesProductsAccidentsOrderStaffCreate(
requestBody: StaffOrderAccidentsCredit,
): CancelablePromise<OrderProduct> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/products/accidents/order-staff/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @deprecated
   * Deprecated. Оформление заказа одним вызовом сохранено только для обратной совместимости и будет отключено 30.11.2026. Актуальный порядок: `POST /sales/products/{code}/price/` (с `session`) -> `PATCH /sales/orders/{id}/` -> `POST /sales/orders/{id}/checkout/`.
   * @param requestBody 
   * @returns OrderProduct 
   * @throws ApiError
   */
  public static salesProductsMedicalTravelOrderBrokerCreate(
requestBody: StaffOrderTravelInsurance,
): CancelablePromise<OrderProduct> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/products/medical_travel/order-broker/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @deprecated
   * Deprecated. Оформление заказа одним вызовом сохранено только для обратной совместимости и будет отключено 30.11.2026. Актуальный порядок: `POST /sales/products/{code}/price/` (с `session`) -> `PATCH /sales/orders/{id}/` -> `POST /sales/orders/{id}/checkout/`.
   * @param requestBody 
   * @returns OrderProduct 
   * @throws ApiError
   */
  public static salesProductsMedicalTravelOrderStaffCreate(
requestBody: StaffOrderTravelInsuranceCredit,
): CancelablePromise<OrderProduct> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/products/medical_travel/order-staff/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param broker Filtrează tranzacțiile după ID-ul brokerului.
   * @param contractor Filtrează tranzacțiile după ID-ul entității contractante.
   * @param createdGte Filtrează tranzacțiile create la sau după data transmisă.
   * @param createdLte Filtrează tranzacțiile create la sau înainte de data transmisă.
   * @param customer Filtrează tranzacțiile după ID-ul clientului.
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param invoice 
   * @param order Filtrează tranzacțiile după ID-ul comenzii.
   * @param ordering Ordering fields: created, modified
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param paymentType Filtrează tranzacțiile după tipul de plată.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param product Filtrează tranzacțiile după ID-urile produselor.
   * @param search Search fields: invoice__order__user__email, invoice__order__user__first_name, invoice__order__user__last_name, invoice__order__customer__email, invoice__order__customer__first_name, invoice__order__customer__last_name, invoice__order__contractor__as_person__email, invoice__order__contractor__as_person__first_name, invoice__order__contractor__as_person__last_name, invoice__order__contractor__as_person__identification_number, invoice__order__contractor__as_legalentity__identification_number, invoice__order__contractor__as_legalentity__name
   * @param status * `created` - Created
 * * `processing` - Processing
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `succeeded` - Succeeded
 * * `canceled` - Canceled
 * * `error` - Error
 * * `refunded` - Refunded
 * * `timeout` - Timeout
 * * `unknown` - Unknown
   * @param user Filtrează tranzacțiile după ID-ul utilizatorului.
   * @returns PaginatedTransactionListList 
   * @throws ApiError
   */
  public static salesTransactionsList(
broker?: number,
contractor?: number,
createdGte?: string,
createdLte?: string,
customer?: number,
excludeIds?: string,
ids?: string,
invoice?: number,
order?: number,
ordering?: string,
page?: number,
pageSize?: number,
paymentType?: Array<'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos'>,
product?: Array<number>,
search?: string,
status?: 'authorized' | 'canceled' | 'created' | 'error' | 'pending' | 'processing' | 'refunded' | 'succeeded' | 'timeout' | 'unknown',
user?: number,
): CancelablePromise<PaginatedTransactionListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/transactions/',
      query: {
        'broker': broker,
        'contractor': contractor,
        'created_gte': createdGte,
        'created_lte': createdLte,
        'customer': customer,
        'exclude_ids': excludeIds,
        'ids': ids,
        'invoice': invoice,
        'order': order,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'payment_type': paymentType,
        'product': product,
        'search': search,
        'status': status,
        'user': user,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this transaction.
   * @returns TransactionDetail 
   * @throws ApiError
   */
  public static salesTransactionsRetrieve(
id: number,
): CancelablePromise<TransactionDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/transactions/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this transaction.
   * @returns TransactionStatus 
   * @throws ApiError
   */
  public static salesTransactionsStatusRetrieve(
id: number,
): CancelablePromise<TransactionStatus> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/transactions/{id}/status/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this transaction.
   * @returns any 
   * @throws ApiError
   */
  public static salesTransactionsTestConfirmCreate(
id: number,
): CancelablePromise<Record<string, any>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/transactions/{id}/test-confirm/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * verify final transaction status from provider
   * @param id A unique integer value identifying this transaction.
   * @returns TransactionDetail 
   * @throws ApiError
   */
  public static salesTransactionsValidateCreate(
id: number,
): CancelablePromise<TransactionDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/transactions/{id}/validate/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param guid 
   * @returns TransactionDetail 
   * @throws ApiError
   */
  public static salesTransactionsByGuidRetrieve(
guid: string,
): CancelablePromise<TransactionDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/transactions/by-guid/{guid}/',
      path: {
        'guid': guid,
      },
    });
  }

  /**
   * @param broker Filtrează tranzacțiile după ID-ul brokerului.
   * @param contractor Filtrează tranzacțiile după ID-ul entității contractante.
   * @param createdGte Filtrează tranzacțiile create la sau după data transmisă.
   * @param createdLte Filtrează tranzacțiile create la sau înainte de data transmisă.
   * @param customer Filtrează tranzacțiile după ID-ul clientului.
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param invoice 
   * @param order Filtrează tranzacțiile după ID-ul comenzii.
   * @param ordering Ordering fields: created, modified
   * @param paymentType Filtrează tranzacțiile după tipul de plată.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param product Filtrează tranzacțiile după ID-urile produselor.
   * @param search Search fields: invoice__order__user__email, invoice__order__user__first_name, invoice__order__user__last_name, invoice__order__customer__email, invoice__order__customer__first_name, invoice__order__customer__last_name, invoice__order__contractor__as_person__email, invoice__order__contractor__as_person__first_name, invoice__order__contractor__as_person__last_name, invoice__order__contractor__as_person__identification_number, invoice__order__contractor__as_legalentity__identification_number, invoice__order__contractor__as_legalentity__name
   * @param status * `created` - Created
 * * `processing` - Processing
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `succeeded` - Succeeded
 * * `canceled` - Canceled
 * * `error` - Error
 * * `refunded` - Refunded
 * * `timeout` - Timeout
 * * `unknown` - Unknown
   * @param user Filtrează tranzacțiile după ID-ul utilizatorului.
   * @returns binary 
   * @throws ApiError
   */
  public static salesTransactionsCsvRetrieve(
broker?: number,
contractor?: number,
createdGte?: string,
createdLte?: string,
customer?: number,
excludeIds?: string,
ids?: string,
invoice?: number,
order?: number,
ordering?: string,
paymentType?: Array<'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos'>,
product?: Array<number>,
search?: string,
status?: 'authorized' | 'canceled' | 'created' | 'error' | 'pending' | 'processing' | 'refunded' | 'succeeded' | 'timeout' | 'unknown',
user?: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/transactions/csv/',
      query: {
        'broker': broker,
        'contractor': contractor,
        'created_gte': createdGte,
        'created_lte': createdLte,
        'customer': customer,
        'exclude_ids': excludeIds,
        'ids': ids,
        'invoice': invoice,
        'order': order,
        'ordering': ordering,
        'payment_type': paymentType,
        'product': product,
        'search': search,
        'status': status,
        'user': user,
      },
    });
  }

  /**
   * @param broker Filtrează tranzacțiile după ID-ul brokerului.
   * @param contractor Filtrează tranzacțiile după ID-ul entității contractante.
   * @param createdGte Filtrează tranzacțiile create la sau după data transmisă.
   * @param createdLte Filtrează tranzacțiile create la sau înainte de data transmisă.
   * @param customer Filtrează tranzacțiile după ID-ul clientului.
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param invoice 
   * @param order Filtrează tranzacțiile după ID-ul comenzii.
   * @param ordering Ordering fields: created, modified
   * @param paymentType Filtrează tranzacțiile după tipul de plată.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param product Filtrează tranzacțiile după ID-urile produselor.
   * @param search Search fields: invoice__order__user__email, invoice__order__user__first_name, invoice__order__user__last_name, invoice__order__customer__email, invoice__order__customer__first_name, invoice__order__customer__last_name, invoice__order__contractor__as_person__email, invoice__order__contractor__as_person__first_name, invoice__order__contractor__as_person__last_name, invoice__order__contractor__as_person__identification_number, invoice__order__contractor__as_legalentity__identification_number, invoice__order__contractor__as_legalentity__name
   * @param status * `created` - Created
 * * `processing` - Processing
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `succeeded` - Succeeded
 * * `canceled` - Canceled
 * * `error` - Error
 * * `refunded` - Refunded
 * * `timeout` - Timeout
 * * `unknown` - Unknown
   * @param user Filtrează tranzacțiile după ID-ul utilizatorului.
   * @returns binary 
   * @throws ApiError
   */
  public static salesTransactionsPdfRetrieve(
broker?: number,
contractor?: number,
createdGte?: string,
createdLte?: string,
customer?: number,
excludeIds?: string,
ids?: string,
invoice?: number,
order?: number,
ordering?: string,
paymentType?: Array<'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos'>,
product?: Array<number>,
search?: string,
status?: 'authorized' | 'canceled' | 'created' | 'error' | 'pending' | 'processing' | 'refunded' | 'succeeded' | 'timeout' | 'unknown',
user?: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/transactions/pdf/',
      query: {
        'broker': broker,
        'contractor': contractor,
        'created_gte': createdGte,
        'created_lte': createdLte,
        'customer': customer,
        'exclude_ids': excludeIds,
        'ids': ids,
        'invoice': invoice,
        'order': order,
        'ordering': ordering,
        'payment_type': paymentType,
        'product': product,
        'search': search,
        'status': status,
        'user': user,
      },
    });
  }

  /**
   * @param broker Filtrează tranzacțiile după ID-ul brokerului.
   * @param contractor Filtrează tranzacțiile după ID-ul entității contractante.
   * @param createdGte Filtrează tranzacțiile create la sau după data transmisă.
   * @param createdLte Filtrează tranzacțiile create la sau înainte de data transmisă.
   * @param customer Filtrează tranzacțiile după ID-ul clientului.
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param invoice 
   * @param order Filtrează tranzacțiile după ID-ul comenzii.
   * @param ordering Ordering fields: created, modified
   * @param paymentType Filtrează tranzacțiile după tipul de plată.
 *
 * * `card` - Card
 * * `cash` - Cash
 * * `mia` - Mia
 * * `cash_mia` - Cash Mia
 * * `pos` - Pos
 * * `broker_settlement` - Broker Settlement
 * * `bank_transfer` - Bank Transfer
 * * `credit` - Internal credit
 * * `manual` - Manual
   * @param product Filtrează tranzacțiile după ID-urile produselor.
   * @param search Search fields: invoice__order__user__email, invoice__order__user__first_name, invoice__order__user__last_name, invoice__order__customer__email, invoice__order__customer__first_name, invoice__order__customer__last_name, invoice__order__contractor__as_person__email, invoice__order__contractor__as_person__first_name, invoice__order__contractor__as_person__last_name, invoice__order__contractor__as_person__identification_number, invoice__order__contractor__as_legalentity__identification_number, invoice__order__contractor__as_legalentity__name
   * @param status * `created` - Created
 * * `processing` - Processing
 * * `pending` - Pending
 * * `authorized` - Funds reserved
 * * `succeeded` - Succeeded
 * * `canceled` - Canceled
 * * `error` - Error
 * * `refunded` - Refunded
 * * `timeout` - Timeout
 * * `unknown` - Unknown
   * @param user Filtrează tranzacțiile după ID-ul utilizatorului.
   * @returns binary 
   * @throws ApiError
   */
  public static salesTransactionsXlsxRetrieve(
broker?: number,
contractor?: number,
createdGte?: string,
createdLte?: string,
customer?: number,
excludeIds?: string,
ids?: string,
invoice?: number,
order?: number,
ordering?: string,
paymentType?: Array<'bank_transfer' | 'broker_settlement' | 'card' | 'cash' | 'cash_mia' | 'credit' | 'manual' | 'mia' | 'pos'>,
product?: Array<number>,
search?: string,
status?: 'authorized' | 'canceled' | 'created' | 'error' | 'pending' | 'processing' | 'refunded' | 'succeeded' | 'timeout' | 'unknown',
user?: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/sales/transactions/xlsx/',
      query: {
        'broker': broker,
        'contractor': contractor,
        'created_gte': createdGte,
        'created_lte': createdLte,
        'customer': customer,
        'exclude_ids': excludeIds,
        'ids': ids,
        'invoice': invoice,
        'order': order,
        'ordering': ordering,
        'payment_type': paymentType,
        'product': product,
        'search': search,
        'status': status,
        'user': user,
      },
    });
  }

  /**
   * @param paymentProvider 
   * @param requestBody 
   * @returns any 
   * @throws ApiError
   */
  public static salesWebhookCreate(
paymentProvider: number,
requestBody?: Record<string, any>,
): CancelablePromise<Record<string, any>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/sales/webhook/{payment_provider}/',
      path: {
        'payment_provider': paymentProvider,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

}
